// Site-wide ambient music player. One <audio> element lives in the
// MusicProvider mounted at the App shell, persists across page navigation
// (since React Router never reloads the document), and exposes simple
// methods for the rest of the app: enable / pause / resume / next / previous /
// volume / mute / duck / unduck / pauseForSlideshow / resumeFromSlideshow.
//
// Browser autoplay rules require a user gesture before audio plays. We
// default to "music enabled" but wait for the first click/key/touch gesture
// before creating audible playback. The first start fades in.
//
// Voice ducking: while a Shao Kahn voice clip is playing we want the
// background music to drop to ~20% volume so the narration cuts through.
// The voice helper (lib/voice.ts) calls duckMusic() before play and
// unduckMusic() in the onended handler.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

// Hardcoded track list. To add more music, drop the file into
// src/client/public/slideshow/ and append to this list.
// Exported so other surfaces (Slideshow.tsx) can use the same rotation
// instead of being hardcoded to a single theme file.
// IMPORTANT: include EVERY MP3 in src/client/public/slideshow/ here so
// the same rotation drives both site-wide playback and slideshow mode.
// theme.mp3 was left out for a stretch when slideshow used it directly
// - that's been fixed; all four are now in the shared pool.
export const MUSIC_TRACKS: string[] = [
  "/slideshow/theme.mp3",
  "/slideshow/acoustic_fuckin_up.mp3",
  "/battle-music/crimson-clash.mp3",
  "/battle-music/neon-combat.mp3",
];
const TRACKS = MUSIC_TRACKS;

export type MusicMode = "focus" | "review" | "after-hours";

const MUSIC_PREF_KEY = "njrc.music.v2";
export const MODE_TRACKS: Record<MusicMode, string[]> = {
  focus: ["/slideshow/theme.mp3", "/slideshow/acoustic_fuckin_up.mp3"],
  review: ["/battle-music/crimson-clash.mp3", "/slideshow/theme.mp3"],
  "after-hours": ["/battle-music/neon-combat.mp3", "/slideshow/acoustic_fuckin_up.mp3"],
};
const MODE_VOLUME: Record<MusicMode, number> = {
  focus: 0.20,
  review: 0.26,
  "after-hours": 0.18,
};
const DUCKED_VOLUME = 0.12;
const FADE_MS = 1200;

interface MusicPref {
  enabled: boolean;
  mode: MusicMode;
  volume: number;
  muted: boolean;
}

function clampVolume(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return 1;
  return Math.max(0, Math.min(1, n));
}

function readReducedSensory(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function readMusicPref(): MusicPref {
  try {
    const raw = localStorage.getItem(MUSIC_PREF_KEY);
    if (!raw) return { enabled: true, mode: "focus", volume: 1, muted: false };
    const parsed = JSON.parse(raw) as {
      enabled?: unknown;
      mode?: unknown;
      volume?: unknown;
      muted?: unknown;
    };
    const mode =
      parsed.mode === "review" || parsed.mode === "after-hours" || parsed.mode === "focus"
        ? parsed.mode
        : "focus";
    return {
      enabled: parsed.enabled !== false,
      mode,
      volume: clampVolume(parsed.volume ?? 1),
      muted: parsed.muted === true,
    };
  } catch {
    return { enabled: true, mode: "focus", volume: 1, muted: false };
  }
}

function writeMusicPref(enabled: boolean, mode: MusicMode, volume: number, muted: boolean): void {
  try {
    localStorage.setItem(
      MUSIC_PREF_KEY,
      JSON.stringify({ enabled, mode, volume: clampVolume(volume), muted }),
    );
  } catch {}
}

/** Pretty-print a track URL into a human title (for the iOS lock-screen). */
export function trackTitleFromUrl(url: string): string {
  const file = url.split("/").pop() || "Track";
  const stem = file.replace(/\.mp3$/i, "");
  return stem
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Push the current track's metadata into the platform Media Session so
 *  it shows on the iOS lock screen / Control Center AND the Android
 *  notification shade / lock screen. Also serves as the signal to the
 *  OS that we're a media app, which unlocks background-advance. */
function setMediaSessionForTrack(trackUrl: string): void {
  if (!("mediaSession" in navigator) || typeof MediaMetadata === "undefined") return;
  try {
    navigator.mediaSession.metadata = new MediaMetadata({
      title: trackTitleFromUrl(trackUrl),
      artist: "NJRC",
      album: "NJRC",
      // Album-art thumbnail. Android Chrome shows it prominently in the
      // notification + lock screen; iOS Safari uses it on the Control
      // Center "now playing" tile. Multiple sizes let the OS pick the
      // closest one to its display density.
      artwork: [
        { src: "/logo.png", sizes: "1254x1254", type: "image/png" },
        { src: "/logo.png", sizes: "512x512", type: "image/png" },
        { src: "/logo.png", sizes: "192x192", type: "image/png" },
      ],
    });
  } catch {
    /* MediaMetadata can throw on some browsers - safe to ignore */
  }
}

interface MusicContextValue {
  enabled: boolean;
  mode: MusicMode;
  volume: number;
  muted: boolean;
  currentTrack: string;
  currentTrackTitle: string;
  /** Live state of the underlying <audio> element - true when actually
   *  emitting sound, false when paused/ended/never started. Updates via
   *  audio event listeners installed in MusicProvider. */
  isPlaying: boolean;
  /** Mark audio as user-allowed and start the player. */
  enable: (mode?: MusicMode) => void;
  setMode: (mode: MusicMode) => void;
  nextTrack: () => void;
  previousTrack: () => void;
  setVolume: (volume: number) => void;
  setMuted: (muted: boolean) => void;
  toggleMute: () => void;
  /** Pause without forgetting the user wanted music. */
  pause: () => void;
  /** Resume after a pause. */
  resume: () => void;
  /** Drop volume for voiceover; auto-restores when ALL ducks released. */
  duck: () => void;
  unduck: () => void;
  /** Slideshow mode handoff. */
  pauseForSlideshow: () => void;
  resumeFromSlideshow: () => void;
  /** If enabled but audio isn't playing (paused/ended/errored), kick a fresh
   *  random track in. No-op if already playing or if not enabled. Safe to
   *  call freely from page-mount useEffects as a self-heal. */
  ensurePlaying: () => void;
}

const MusicContext = createContext<MusicContextValue | null>(null);

export function useMusic(): MusicContextValue {
  const v = useContext(MusicContext);
  if (!v) throw new Error("useMusic outside MusicProvider");
  return v;
}

export function MusicProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const duckCountRef = useRef(0);
  const slideshowPausedRef = useRef(false);
  const fadeTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const playOffsetRef = useRef<(offset: number) => void>(() => {});
  const initialPref = useMemo(() => readMusicPref(), []);
  const initialTrack = (MODE_TRACKS[initialPref.mode] ?? TRACKS)[0] ?? "";
  const [enabled, setEnabled] = useState<boolean>(initialPref.enabled && !readReducedSensory());
  const [mode, setModeState] = useState<MusicMode>(initialPref.mode);
  const [volume, setVolumeState] = useState<number>(initialPref.volume);
  const [muted, setMutedState] = useState<boolean>(initialPref.muted || initialPref.volume <= 0);
  const [currentTrack, setCurrentTrack] = useState<string>(initialTrack);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const enabledRef = useRef(enabled);
  const modeRef = useRef(mode);
  const volumeRef = useRef(volume);
  const mutedRef = useRef(muted);
  const currentIndexRef = useRef(currentIndex);
  const currentTrackRef = useRef(currentTrack);

  useEffect(() => { enabledRef.current = enabled; }, [enabled]);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { volumeRef.current = volume; }, [volume]);
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
  useEffect(() => { currentTrackRef.current = currentTrack; }, [currentTrack]);

  // Lazy-create the audio element on first enable. Single element reused
  // for the lifetime of the SPA - survives React Router navigations.
  const ensureAudio = useCallback(() => {
    if (audioRef.current) return audioRef.current;
    const a = new Audio();
    a.loop = false;        // we manually advance to the next track
    a.volume = 0;          // start silent; fade in on play
    a.preload = "auto";
    a.crossOrigin = "anonymous";
    audioRef.current = a;
    a.addEventListener("ended", () => {
      playOffsetRef.current(1);
    });
    // Whenever a new track starts playing, prefetch the others so the
    // NEXT advance is instant (no gap between tracks while the browser
    // fetches MP3 bytes from the server). Combined with the `ended`
    // listener above this gives us seamless rotation.
    a.addEventListener("playing", () => {
      try { prefetchUpcomingTracks(a.src); } catch { /* non-fatal */ }
    });
    // If a track errors out mid-stream (network blip, codec hiccup,
    // bad file), don't just stop - pick a different random track and
    // keep going. Without this, a single 404 on a track silently
    // killed the whole rotation until the user navigated.
    a.addEventListener("error", () => {
      // eslint-disable-next-line no-console
      console.warn("[music] audio error - advancing to next track");
      playOffsetRef.current(1);
    });
    // Reflect actual playback state into React so the header toggle
    // shows the right icon. Listening to play/playing/pause/ended
    // covers all the transitions browsers fire.
    a.addEventListener("play", () => {
      setIsPlaying(true);
      if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "playing";
    });
    a.addEventListener("playing", () => {
      setIsPlaying(true);
      if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "playing";
    });
    a.addEventListener("pause", () => {
      setIsPlaying(false);
      if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "paused";
    });
    a.addEventListener("ended", () => setIsPlaying(false));
    // Wire up Media Session action handlers. CRITICAL for iOS Safari
    // background playback: when the screen is locked, iOS suspends the
    // ability to call audio.play() from arbitrary JS callbacks, including
    // the `ended` handler that advances to the next track. Without
    // MediaSession, the next-track sequence stutters because iOS thinks
    // we're trying to autoplay without permission. Registering as a
    // proper "media app" via these handlers tells iOS we ARE a player
    // and unlocks the background-advance permission.
    if ("mediaSession" in navigator) {
      try {
        navigator.mediaSession.setActionHandler("play", () => {
          a.play().catch(() => {});
        });
        navigator.mediaSession.setActionHandler("pause", () => {
          a.pause();
        });
        navigator.mediaSession.setActionHandler("nexttrack", () => {
          playOffsetRef.current(1);
        });
        navigator.mediaSession.setActionHandler("previoustrack", () => {
          playOffsetRef.current(-1);
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[music] mediaSession setup failed:", err);
      }
    }
    return a;
  }, []);

  // Pre-fetch one of the OTHER tracks (not the currently-playing one) so
  // when the audio element advances on `ended`, the browser already has
  // the bytes in cache and there's no audible gap. We use a fresh hidden
  // <link rel="prefetch"> per track so the browser handles dedup.
  // Called once at mount and re-called whenever the track changes.
  const prefetchUpcomingTracks = useCallback((excludeUrl: string) => {
    // ONLY prefetch ONE upcoming track. The previous version prefetched
    // ALL 6 others on every track-change, totalling ~50MB of parallel
    // HTTP streams from the browser. On the 2GB droplet that - combined
    // with stats, SSE, and page loads - was enough to OOM-kill the
    // node process. One next-track is plenty for "no gap when the
    // current song ends" - that's the only thing this prefetch needs
    // to enable.
    const candidates = TRACKS.filter((t) => !excludeUrl.endsWith(t));
    const next = candidates[Math.floor(Math.random() * candidates.length)];
    if (!next) return;
    // Avoid double-injecting prefetch links for the same file across
    // re-fires. The browser dedups but every link element accumulates
    // in <head> indefinitely otherwise.
    const existing = document.head.querySelector(`link[rel="prefetch"][href="${next}"]`);
    if (existing) return;
    const link = document.createElement("link");
    link.rel = "prefetch";
    link.as = "audio";
    link.href = next;
    document.head.appendChild(link);
  }, []);

  // Smooth volume tween. Cancels any in-flight fade.
  const fadeTo = useCallback((target: number, ms = FADE_MS) => {
    const a = audioRef.current;
    if (!a) return;
    if (fadeTimerRef.current) clearInterval(fadeTimerRef.current);
    const nextTarget = clampVolume(target);
    if (ms <= 0) {
      a.volume = nextTarget;
      fadeTimerRef.current = null;
      return;
    }
    const start = a.volume;
    const startedAt = Date.now();
    fadeTimerRef.current = setInterval(() => {
      const t = Math.min(1, (Date.now() - startedAt) / ms);
      a.volume = start + (nextTarget - start) * t;
      if (t >= 1) {
        clearInterval(fadeTimerRef.current!);
        fadeTimerRef.current = null;
      }
    }, 30);
  }, []);

  const targetVolume = useCallback((requestedMode: MusicMode = modeRef.current) => {
    if (slideshowPausedRef.current) return 0;
    const userLevel = mutedRef.current ? 0 : clampVolume(volumeRef.current);
    const full = (MODE_VOLUME[requestedMode] ?? MODE_VOLUME.focus) * userLevel;
    return duckCountRef.current > 0 ? Math.min(full, DUCKED_VOLUME * userLevel) : full;
  }, []);

  const persist = useCallback(() => {
    writeMusicPref(enabledRef.current, modeRef.current, volumeRef.current, mutedRef.current);
  }, []);

  const playTrackAt = useCallback((index: number, requestedMode: MusicMode = modeRef.current, fadeMs = FADE_MS) => {
    if (readReducedSensory()) return;
    const trackPool = MODE_TRACKS[requestedMode] ?? TRACKS;
    if (trackPool.length === 0) return;
    const normalized = ((index % trackPool.length) + trackPool.length) % trackPool.length;
    const next = trackPool[normalized]!;
    modeRef.current = requestedMode;
    currentIndexRef.current = normalized;
    currentTrackRef.current = next;
    setModeState(requestedMode);
    setCurrentIndex(normalized);
    setCurrentTrack(next);
    const a = ensureAudio();
    if (!a.src.endsWith(next)) a.src = next;
    else {
      try { a.currentTime = 0; } catch { /* browser may reject before metadata */ }
    }
    setMediaSessionForTrack(next);
    a.play().catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("[music] play() blocked:", err);
    });
    fadeTo(targetVolume(requestedMode), fadeMs);
  }, [ensureAudio, fadeTo, targetVolume]);

  playOffsetRef.current = (offset: number) => {
    playTrackAt(currentIndexRef.current + offset, modeRef.current, offset === 0 ? FADE_MS : 700);
  };

  const nextTrack = useCallback(() => {
    if (readReducedSensory()) return;
    if (!enabledRef.current) {
      enabledRef.current = true;
      setEnabled(true);
      persist();
    }
    playOffsetRef.current(1);
  }, [persist]);

  const previousTrack = useCallback(() => {
    if (readReducedSensory()) return;
    if (!enabledRef.current) {
      enabledRef.current = true;
      setEnabled(true);
      persist();
    }
    playOffsetRef.current(-1);
  }, [persist]);

  const enable = useCallback((nextMode: MusicMode = mode) => {
    if (readReducedSensory()) {
      enabledRef.current = false;
      setEnabled(false);
      persist();
      return;
    }
    const trackPool = MODE_TRACKS[nextMode] ?? TRACKS;
    const index = trackPool.findIndex((track) => track === currentTrackRef.current);
    enabledRef.current = true;
    modeRef.current = nextMode;
    setModeState(nextMode);
    setEnabled(true);
    persist();
    playTrackAt(index >= 0 ? index : 0, nextMode, FADE_MS);
  }, [mode, persist, playTrackAt]);

  const setMode = useCallback((nextMode: MusicMode) => {
    modeRef.current = nextMode;
    setModeState(nextMode);
    persist();
    const trackPool = MODE_TRACKS[nextMode] ?? TRACKS;
    const current = currentTrackRef.current;
    const index = trackPool.findIndex((track) => track === current);
    currentIndexRef.current = index >= 0 ? index : 0;
    setCurrentIndex(currentIndexRef.current);
    setCurrentTrack(trackPool[currentIndexRef.current] ?? current);
    if (!enabledRef.current) return;
    playTrackAt(currentIndexRef.current, nextMode, 700);
  }, [persist, playTrackAt]);

  const setVolume = useCallback((nextVolume: number) => {
    const next = clampVolume(nextVolume);
    const nextMuted = next <= 0 ? true : false;
    volumeRef.current = next;
    mutedRef.current = nextMuted;
    setVolumeState(next);
    setMutedState(nextMuted);
    persist();
    if (audioRef.current) fadeTo(targetVolume(), 180);
  }, [fadeTo, persist, targetVolume]);

  const setMuted = useCallback((nextMuted: boolean) => {
    mutedRef.current = nextMuted;
    setMutedState(nextMuted);
    if (!nextMuted && volumeRef.current <= 0) {
      volumeRef.current = 1;
      setVolumeState(1);
    }
    persist();
    if (audioRef.current) fadeTo(targetVolume(), nextMuted ? 140 : 420);
  }, [fadeTo, persist, targetVolume]);

  const toggleMute = useCallback(() => {
    setMuted(!mutedRef.current);
  }, [setMuted]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    enabledRef.current = false;
    setEnabled(false);
    persist();
  }, [persist]);
  const resume = useCallback(() => {
    if (readReducedSensory()) return;
    enabledRef.current = true;
    setEnabled(true);
    persist();
    const a = audioRef.current;
    if (!a) {
      playTrackAt(currentIndexRef.current, modeRef.current, FADE_MS);
      return;
    }
    a.play().catch(() => {});
    fadeTo(targetVolume());
  }, [fadeTo, persist, playTrackAt, targetVolume]);

  const duck = useCallback(() => {
    duckCountRef.current++;
    if (audioRef.current) fadeTo(targetVolume(), 250);
  }, [fadeTo, targetVolume]);
  const unduck = useCallback(() => {
    duckCountRef.current = Math.max(0, duckCountRef.current - 1);
    if (audioRef.current) fadeTo(targetVolume(), 600);
  }, [fadeTo, targetVolume]);

  /**
   * Self-heal entrypoint. The audio element occasionally stops on its own -
   * track load error, browser background tab suspension, transient
   * network blip. This method, called from page-mount useEffects, checks
   * the player and kicks a fresh track if needed. Never restarts voice.
   */
  const ensurePlaying = useCallback(() => {
    if (!enabledRef.current) return;
    if (slideshowPausedRef.current) return; // slideshow owns the audio stage
    const a = audioRef.current ?? ensureAudio();
    // "Should be playing" but isn't → restart with a fresh random track.
    // We treat paused, ended, no-src, and errored as states needing a kick.
    const stalled = a.paused || a.ended || !a.src || a.error !== null;
    if (!stalled) return;
    if (a.src && !a.ended && a.error === null) {
      a.play().catch(() => {});
    } else {
      playTrackAt(currentIndexRef.current, modeRef.current, 800);
    }
    fadeTo(targetVolume(), 800);
  }, [ensureAudio, playTrackAt, fadeTo, targetVolume]);

  const pauseForSlideshow = useCallback(() => {
    slideshowPausedRef.current = true;
    fadeTo(0, 400);
    setTimeout(() => audioRef.current?.pause(), 450);
  }, [fadeTo]);
  const resumeFromSlideshow = useCallback(() => {
    slideshowPausedRef.current = false;
    if (!enabledRef.current || !audioRef.current) return;
    // Pick a fresh track so the user doesn't hear the same loop they had on
    // before slideshow.
    playOffsetRef.current(1);
    fadeTo(targetVolume(), 800);
  }, [fadeTo, targetVolume]);

  useEffect(() => {
    if (!enabled) return;
    if (readReducedSensory()) {
      audioRef.current?.pause();
      enabledRef.current = false;
      setEnabled(false);
      persist();
      return;
    }
    if (isPlaying) return;
    const startOnGesture = () => {
      if (!enabledRef.current || readReducedSensory()) return;
      const a = audioRef.current;
      if (a?.src && !a.ended && a.error === null) {
        a.play().catch(() => {});
        fadeTo(targetVolume(), FADE_MS);
        return;
      }
      playTrackAt(currentIndexRef.current, modeRef.current, FADE_MS);
    };
    window.addEventListener("pointerdown", startOnGesture, { once: true, capture: true });
    window.addEventListener("keydown", startOnGesture, { once: true, capture: true });
    window.addEventListener("touchstart", startOnGesture, { once: true, capture: true });
    return () => {
      window.removeEventListener("pointerdown", startOnGesture, { capture: true });
      window.removeEventListener("keydown", startOnGesture, { capture: true });
      window.removeEventListener("touchstart", startOnGesture, { capture: true });
    };
  }, [enabled, isPlaying, fadeTo, persist, playTrackAt, targetVolume]);

  // Cleanup on unmount (which only happens at SPA shutdown).
  useEffect(() => {
    return () => {
      if (fadeTimerRef.current) clearInterval(fadeTimerRef.current);
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  // NO PERIODIC HEARTBEAT. An earlier version called ensurePlaying every
  // 5s as a "self-heal" but that broke the contract that PAUSE means
  // PAUSE - if the user hit pause, or started music in another app, the
  // heartbeat would barge back in 5 seconds later and reassert itself.
  // The `ended` event listener (above) handles legitimate track-end
  // advances; `error` handles broken-track recovery. Music starts from
  // the first user gesture when enabled, or from explicit player controls.
  // User pause is final until they un-pause.

  const value = useMemo<MusicContextValue>(
    () => ({
      enabled,
      mode,
      volume,
      muted,
      currentTrack,
      currentTrackTitle: trackTitleFromUrl(currentTrack),
      isPlaying,
      enable,
      setMode,
      nextTrack,
      previousTrack,
      setVolume,
      setMuted,
      toggleMute,
      pause,
      resume,
      duck,
      unduck,
      pauseForSlideshow,
      resumeFromSlideshow,
      ensurePlaying,
    }),
    [
      enabled,
      mode,
      volume,
      muted,
      currentTrack,
      isPlaying,
      enable,
      setMode,
      nextTrack,
      previousTrack,
      setVolume,
      setMuted,
      toggleMute,
      pause,
      resume,
      duck,
      unduck,
      pauseForSlideshow,
      resumeFromSlideshow,
      ensurePlaying,
    ],
  );

  return <MusicContext.Provider value={value}>{children}</MusicContext.Provider>;
}
